import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faArrowUp,
  faArrowDown,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

const categoryColor = "#144677";

const getPercentageColor = (val) => {
  return val >= 0 ? "#22c55e" : "#ef4444";
};

const StagePerformanceCards = ({ data }) => {
  if (!data || data.length === 0) return null;

  return (
    <div className="p-6">
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {data.map((item, index) => {
            const isTotal = item.stage === "TOTAL";
            const isMeetingTarget = item.percentage_to_target >= 0;

            return (
              <div
                key={index}
                className={`rounded-lg p-4 border transition-all duration-300 hover:scale-105 cursor-pointer
                  ${
                    isTotal
                      ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300 shadow-lg"
                      : "bg-white border-gray-200 hover:shadow-xl hover:border-blue-200"
                  }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center 
                                 text-white text-sm font-bold shadow-md"
                      style={{ backgroundColor: categoryColor }}
                    >
                      {item.stage
                        .split(" ")
                        .map((w) => w[0])
                        .join("")}
                    </div>

                    <div>
                      <h3
                        className="text-sm font-bold"
                        style={{ color: categoryColor }}
                      >
                        {item.stage}
                      </h3>
                      {isTotal && (
                        <span className="text-xs text-blue-600 font-medium">
                          Overall Summary
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status Icon */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isMeetingTarget ? "bg-green-100" : "bg-red-100"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={isMeetingTarget ? faCheck : faClock}
                      className={`text-sm ${
                        isMeetingTarget ? "text-green-600" : "text-red-600"
                      }`}
                    />
                  </div>
                </div>

                {/* Time Metrics */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-gray-500 text-xs"
                      />
                      <span className="text-xs text-gray-600 font-medium">
                        Average Time
                      </span>
                    </div>
                    <span
                      className="font-bold text-sm"
                      style={{ color: categoryColor }}
                    >
                      {item.average}h
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="text-gray-500 text-xs"
                      />
                      <span className="text-xs text-gray-600 font-medium">
                        Target Time
                      </span>
                    </div>
                    <span
                      className="font-bold text-sm"
                      style={{ color: categoryColor }}
                    >
                      {item.target}h
                    </span>
                  </div>
                </div>

                {/* Performance Badge */}
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-medium">
                      Performance
                    </span>

                    <div className="flex items-center gap-1">
                      <FontAwesomeIcon
                        icon={isMeetingTarget ? faArrowUp : faArrowDown}
                        className={`text-xs ${
                          isMeetingTarget ? "text-green-600" : "text-red-600"
                        }`}
                      />
                      <span
                        className="text-lg font-bold"
                        style={{
                          color: getPercentageColor(item.percentage_to_target),
                        }}
                      >
                        {item.percentage_to_target > 0 ? "+" : ""}
                        {item.percentage_to_target}%
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${Math.min(
                            Math.abs(item.percentage_to_target),
                            100
                          )}%`,
                          backgroundColor: getPercentageColor(
                            item.percentage_to_target
                          ),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StagePerformanceCards;
